import { logger } from "@/lib/logger";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SubmissionCard } from "./SubmissionCard";
import { SubmissionFilters, StatusFilter, SortOrder } from "./SubmissionFilters";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Inbox, Mail, FileImage, Loader2, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import type { Tables } from "@/integrations/supabase/types";

type Submission = Tables<"conversation_submissions"> & {
  reviewed_at?: string | null;
};

const ITEMS_PER_PAGE = 10;

export function AdminSubmissions() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [allExpanded, setAllExpanded] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchSubmissions = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("conversation_submissions")
        .select("*")
        .order("submitted_at", { ascending: false });

      if (error) {
        logger.error("Error fetching submissions:", error);
        toast({ title: "Error", description: "Failed to load submissions", variant: "destructive" });
      } else {
        setSubmissions(data || []);
      }
      setIsLoading(false);
    };

    fetchSubmissions();

    const channel = supabase
      .channel("submissions-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "conversation_submissions" }, (payload) => {
        if (payload.eventType === "INSERT") {
          setSubmissions((prev) => [payload.new as Submission, ...prev]);
        } else if (payload.eventType === "UPDATE") {
          setSubmissions((prev) => prev.map((s) => (s.id === payload.new.id ? (payload.new as Submission) : s)));
        } else if (payload.eventType === "DELETE") {
          setSubmissions((prev) => prev.filter((s) => s.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const handleStatusChange = async (id: string, status: string) => {
    const updates: { status: string; reviewed_at?: string | null } = { status };
    if (status === "reviewed") updates.reviewed_at = new Date().toISOString();
    else if (status === "new") updates.reviewed_at = null;

    const { error } = await supabase.from("conversation_submissions").update(updates).eq("id", id);
    if (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    } else {
      toast({ title: "Updated", description: `Submission marked as ${status}` });
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("conversation_submissions").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete submission", variant: "destructive" });
    } else {
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      toast({ title: "Deleted", description: "Submission removed" });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setIsDeleting(true);
    const ids = Array.from(selectedIds);
    const { error } = await supabase.from("conversation_submissions").delete().in("id", ids);
    if (error) {
      toast({ title: "Error", description: "Failed to delete submissions", variant: "destructive" });
    } else {
      setSubmissions((prev) => prev.filter((s) => !selectedIds.has(s.id)));
      setSelectedIds(new Set());
      toast({ title: "Deleted", description: `${ids.length} submission(s) removed` });
    }
    setIsDeleting(false);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedSubmissions.map((s) => s.id);
    const allSelected = pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const filteredSubmissions = useMemo(() => {
    let result = [...submissions];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((s) => s.message?.toLowerCase().includes(query) || s.email?.toLowerCase().includes(query));
    }
    if (statusFilter !== "all") result = result.filter((s) => s.status === statusFilter);
    result.sort((a, b) => {
      const dateA = new Date(a.submitted_at).getTime();
      const dateB = new Date(b.submitted_at).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
    return result;
  }, [submissions, searchQuery, statusFilter, sortOrder]);

  const totalPages = Math.ceil(filteredSubmissions.length / ITEMS_PER_PAGE);
  const paginatedSubmissions = filteredSubmissions.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const allPageSelected = paginatedSubmissions.length > 0 && paginatedSubmissions.every((s) => selectedIds.has(s.id));

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, sortOrder]);

  const stats = useMemo(() => ({
    total: submissions.length,
    newCount: submissions.filter((s) => s.status === "new").length,
    withFiles: submissions.filter((s) => s.file_urls && s.file_urls.length > 0).length,
    withEmail: submissions.filter((s) => s.email).length,
  }), [submissions]);

  return (
    <div className="space-y-5">
      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { icon: <Inbox className="h-4 w-4 text-primary" />, value: stats.total, label: "Total" },
          { icon: <span className="text-sm font-bold text-primary">{stats.newCount}</span>, value: stats.newCount, label: "New" },
          { icon: <FileImage className="h-4 w-4 text-foreground" />, value: stats.withFiles, label: "With Files" },
          { icon: <Mail className="h-4 w-4 text-foreground" />, value: stats.withEmail, label: "With Email" },
        ].map((stat) => (
          <div key={stat.label} className="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              {stat.icon}
            </div>
            <div>
              <p className="text-lg font-semibold leading-none">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <SubmissionFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        sortOrder={sortOrder}
        onSortChange={setSortOrder}
      />

      {/* Toolbar: select all, bulk delete, expand/collapse */}
      {!isLoading && paginatedSubmissions.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={allPageSelected}
              onCheckedChange={toggleSelectAll}
              aria-label="Select all on this page"
            />
            <span className="text-xs text-muted-foreground">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select all"}
            </span>
            {selectedIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={handleBulkDelete}
                disabled={isDeleting}
              >
                {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Delete ({selectedIds.size})
              </Button>
            )}
          </div>
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => setAllExpanded((v) => !v)}>
            {allExpanded ? "Collapse All" : "Expand All"}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : paginatedSubmissions.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card py-12 text-center">
          <Inbox className="mb-4 h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No submissions found</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {searchQuery || statusFilter !== "all" ? "Try adjusting your filters" : "Submissions will appear here"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {paginatedSubmissions.map((submission) => (
            <div key={submission.id} className="flex items-start gap-3">
              <Checkbox
                checked={selectedIds.has(submission.id)}
                onCheckedChange={() => toggleSelect(submission.id)}
                className="mt-3.5"
                aria-label={`Select submission from ${submission.email || "unknown"}`}
              />
              <div className="flex-1 min-w-0">
                <SubmissionCard submission={submission} onStatusChange={handleStatusChange} onDelete={handleDelete} forceOpen={allExpanded} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => totalPages <= 5 || page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
              .map((page, idx, arr) => (
                <PaginationItem key={page}>
                  {idx > 0 && arr[idx - 1] !== page - 1 && <span className="px-2 text-muted-foreground">...</span>}
                  <PaginationLink onClick={() => setCurrentPage(page)} isActive={currentPage === page} className="cursor-pointer">{page}</PaginationLink>
                </PaginationItem>
              ))}
            <PaginationItem>
              <PaginationNext onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
